import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../assets/assetKeys';
import { getDialogueById } from '../data/dialogues';
import { InteractionContext, Interactable } from '../interfaces/Interactable';
import { UIStyle } from '../ui/UIStyle';

export type NPCType =
  | 'kakek_sakti'
  | 'ibundo'
  | 'lansia'
  | 'ibu_hamil'
  | 'warga'
  | 'petugas'
  | 'kera'
  | 'harimau';

const NPC_TEXTURE_MAP: Record<NPCType, string> = {
  kakek_sakti: TEXTURE_KEYS.KAKEK_SAKTI,
  ibundo: TEXTURE_KEYS.IBUNDO,
  lansia: TEXTURE_KEYS.LANSIA,
  ibu_hamil: TEXTURE_KEYS.IBU_HAMIL,
  warga: TEXTURE_KEYS.WARGA,
  petugas: TEXTURE_KEYS.PETUGAS,
  kera: TEXTURE_KEYS.KERA,
  harimau: TEXTURE_KEYS.HARIMAU,
};

export interface NPCConfig {
  id?: string;
  name: string;
  type: NPCType;
  dialogueId?: string;
  interactionRadius?: number;
  questCompleteId?: string;
  showLabel?: boolean;
  onInteract?: (ctx: InteractionContext) => void;
}

export class NPC extends Phaser.Physics.Arcade.Sprite implements Interactable {
  readonly interactId: string;
  readonly npcName: string;
  readonly dialogueId?: string;
  readonly npcType: NPCType;
  private interactionRadius: number;
  private questCompleteId?: string;
  private customInteract?: (ctx: InteractionContext) => void;
  private nameLabel?: Phaser.GameObjects.Text;
  private interactEnabled = true;
  private labelOffsetY = 52;
  private walkTween?: Phaser.Tweens.Tween;
  private isWalking = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    config: NPCConfig,
  ) {
    const texture = NPC_TEXTURE_MAP[config.type];
    const frame = scene.textures.getFrame(texture);
    const footY = y + frame.height / 2;

    super(scene, x, footY, texture);

    this.interactId = config.id ?? `npc_${config.type}_${x}_${y}`;
    this.npcName = config.name;
    this.dialogueId = config.dialogueId;
    this.npcType = config.type;
    this.interactionRadius = config.interactionRadius ?? 90;
    this.questCompleteId = config.questCompleteId;
    this.customInteract = config.onInteract;

    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.setOrigin(0.5, 1);
    this.setImmovable(true);
    this.setDepth(8);
    this.labelOffsetY = frame.height + 10;

    if (config.showLabel !== false) {
      const labelY = footY - frame.height - 10;
      this.nameLabel = scene.add.text(x, labelY, config.name, UIStyle.nameTag(14));
      this.nameLabel.setOrigin(0.5, 1);
      this.nameLabel.setDepth(9);
    }

    this.syncPhysicsBody(frame);
  }

  private syncPhysicsBody(frame: Phaser.Textures.Frame): void {
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (!body) return;
    body.setSize(frame.width * 0.5, frame.height * 0.28);
    body.setOffset(frame.width * 0.25, frame.height * 0.72);
    body.updateFromGameObject();
  }

  getInteractPosition(): Phaser.Math.Vector2 {
    return new Phaser.Math.Vector2(this.x, this.y - 20);
  }

  getInteractionRadius(): number {
    return this.interactionRadius;
  }

  setOnInteract(callback: (ctx: InteractionContext) => void): void {
    this.customInteract = callback;
  }

  setInteractEnabled(enabled: boolean): void {
    this.interactEnabled = enabled;
    this.setAlpha(enabled ? 1 : 0.85);
  }

  canInteract(): boolean {
    return this.interactEnabled && this.active && this.visible;
  }

  getPromptText(): string {
    return 'Tekan E / Tap Aksi';
  }

  onInteract(ctx: InteractionContext): void {
    if (this.customInteract) {
      this.customInteract(ctx);
      return;
    }

    if (this.dialogueId) {
      const dialogue = getDialogueById(this.dialogueId);
      if (dialogue) {
        ctx.dialogue.show(dialogue, () => {
          if (this.questCompleteId && ctx.quest.getStatus(this.questCompleteId) !== 'completed') {
            ctx.quest.completeQuest(this.questCompleteId);
          }
        });
      }
    }
  }

  setWalking(moving: boolean, facingLeft = false): void {
    if (moving) {
      this.setFlipX(facingLeft);
    }

    if (moving && !this.isWalking) {
      this.isWalking = true;
      this.walkTween = this.scene.tweens.add({
        targets: this,
        angle: { from: -3, to: 3 },
        duration: 180,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    } else if (!moving && this.isWalking) {
      this.isWalking = false;
      this.walkTween?.stop();
      this.walkTween = undefined;
      this.setAngle(0);
    }
  }

  updateLabels(): void {
    this.nameLabel?.setPosition(this.x, this.y - this.labelOffsetY);
  }

  destroy(fromScene?: boolean): void {
    this.walkTween?.stop();
    this.nameLabel?.destroy();
    super.destroy(fromScene);
  }
}
